import { AlertV2, ButtonV2 } from '@cimplico/components';
import { useEffect, useMemo, useState } from 'react';
import { SubscriptionV2 } from '../../../../types/firm';
import { formatDate } from '../../../../utilities/formatting';
import ConfirmPlanChanges from './ConfirmPlanChanges';
import plans from './plans.json';
import ScheduledChangesAlert from './ScheduledChangeAlert';
import PlanConfiguration from './PlanConfiguration';
import { calculatePlan, formatCost } from './utils';

type PlanSectionProps = {
    subscription: SubscriptionV2;
};

function PlanSection({ subscription }: PlanSectionProps): JSX.Element {
    const [activeEntities, setActiveEntities] = useState<number>(subscription.totalActiveEntityAllocation);
    const [interval, setInterval] = useState<string>(subscription.interval);
    const [confirmChange, setConfirmChange] = useState(false);
    const [showScheduledChanges, setShowScheduledChanges] = useState(false);

    const plan = useMemo(() => calculatePlan(activeEntities), [activeEntities]);
    const readOnly = Boolean(
        subscription.scheduledChange || (subscription.status !== 'ACTIVE' && subscription.status !== 'TRIALING') || subscription.cancelAt
    );
    useEffect(() => {
        setActiveEntities(subscription.totalActiveEntityAllocation);
        setInterval(subscription.interval);
    }, [subscription]);

    /**
     * Determines if the changes the user is making requires an actual subscription change.
     * The only time it doesn't warrant a subscription change is if the active entity count is lower than the next tier, but works out to be more expensive on the lower tier + x additional slots.
     * @example Going from 250 entities (base silver tier) to 200 entities doesn't need a change as it's still cheaper to remain on silver rather than changing to starter + additional slots.
     */
    const eligibleChange = useMemo(() => {
        // An interval change requires a subscription update
        const intervalChanged = interval !== subscription.interval;
        // Changing the base tier requires a subscription update
        const basePlanChanged = plan.name.toLowerCase() !== subscription.tier.toLowerCase();
        // Increasing the active entity allocation requires a change
        const entityAllocationIncrease = activeEntities > subscription.totalActiveEntityAllocation;
        // Decreasing an additional entity slot requires a change
        const additionalEntityAllocationDecreased =
            activeEntities < subscription.totalActiveEntityAllocation && subscription.additionalEntitySlots > 0;

        return intervalChanged || basePlanChanged || entityAllocationIncrease || additionalEntityAllocationDecreased;
    }, [activeEntities, interval, plan.name, subscription]);

    return (
        <div>
            {confirmChange && (
                <ConfirmPlanChanges
                    onClose={(): void => setConfirmChange(false)}
                    activeEntities={activeEntities}
                    interval={interval}
                    subscription={subscription}
                />
            )}
            {showScheduledChanges && (
                <ScheduledChangesAlert subscription={subscription} onClose={(): void => setShowScheduledChanges(false)} />
            )}
            {subscription.scheduledChange && (
                <div>
                    <AlertV2
                        className="mb-4"
                        type="warn"
                        title="Scheduled changes"
                        description={`Your plan will be changed on the next billing cycle (${formatDate(
                            subscription.scheduledChange.updateScheduledAt
                        )}). You cannot make edits to your subscription while a change is scheduled.`}
                    >
                        <AlertV2.Action label="View Scheduled Changes" onClick={(): void => setShowScheduledChanges(true)} />
                    </AlertV2>
                </div>
            )}

            <div className="flex gap-16 py-2">
                <div>
                    <p className="text-sm text-contentSecondary font-medium">Current plan</p>
                    <p className="capitalize text-contentPrimary  font-bold text-lg">{plans[subscription.tier].name}</p>
                </div>
                <div>
                    <p className="text-sm text-contentSecondary font-medium">Included Entities</p>
                    <p className="capitalize text-contentPrimary  font-bold text-lg">{subscription.baseActiveEntityAllocation}</p>
                </div>
                <div>
                    <p className="text-sm text-contentSecondary font-medium">Additional Entities</p>
                    <p className="capitalize text-contentPrimary  font-bold text-lg">
                        {Math.max(subscription.totalActiveEntityAllocation - subscription.baseActiveEntityAllocation, 0)}
                    </p>
                </div>
                <div>
                    <p className="text-sm text-contentSecondary font-medium">Price</p>
                    <p className="capitalize text-contentPrimary  font-bold text-lg">
                        {formatCost(subscription.interval, subscription.totalActiveEntityAllocation, plans[subscription.tier])}
                        <span className="font-normal text-contentSecondary text-sm lowercase">
                            /{subscription.interval === 'MONTHLY' ? 'month' : 'year'}
                        </span>
                    </p>
                </div>
            </div>
            <hr className="my-4" />
            <div className={`relative ${readOnly && 'opacity-50'}`}>
                <h2 className="text-xl font-bold text-contentPrimary text-center">Upgrade your plan</h2>
                {eligibleChange && (
                    <div className="absolute top-0 right-0">
                        <ButtonV2
                            size="sm"
                            label="Confirm changes"
                            onClick={(): void => {
                                setConfirmChange(true);
                            }}
                        />
                    </div>
                )}
                <PlanConfiguration
                    subscription={subscription}
                    interval={interval}
                    activeEntities={activeEntities}
                    setActiveEntities={setActiveEntities}
                    setInterval={setInterval}
                    readOnly={readOnly}
                />
            </div>
        </div>
    );
}

export default PlanSection;
